import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { StatusModel, TerminationReasonModel } from '@utmost.co/enterprise-models';
import { OptionTypeBase, Dialog, Input, Select, Button } from '@beeline.com/bp-design-system';
import { convertToSnakeCase } from 'src/utils/textUtil';
import { useAddTerminationReasons } from './hooks/useAddTerminationReasons';
import { usePatchTerminationReasons } from './hooks/usePatchTerminationReasons';
import { useTerminationReasonsIntl } from './translations/useTerminationReasonsIntl';
interface Props {
  onClose(): void;
  onDone: (terminationReason: TerminationReasonModel) => void;
  terminationReason: TerminationReasonModel | null;
}
export const TerminationReasonsAddModal: FC<React.PropsWithChildren<Props>> = ({
  onClose,
  onDone,
  terminationReason
}) => {
  const { addTerminationReason, loading: isAdding } = useAddTerminationReasons();
  const { patchTerminationReason, loading: isPatching } = usePatchTerminationReasons();
  const intl = useTerminationReasonsIntl();
  const [localTerminationReason, setLocalTerminationReason] = useState<Partial<TerminationReasonModel>>(
    terminationReason
      ? terminationReason
      : {
          assignable: true,
          status: 'ACTIVE',
          eligibleForRehire: true
        }
  );
  const [formSubmitted, setFormSubmitted] = useState(false);
  const handleSubmitForm = useCallback(async () => {
    const { uid, assignable, name, onExportMapTo, status, referenceId, eligibleForRehire } =
      localTerminationReason as TerminationReasonModel;
    setFormSubmitted(true);
    if (!name || !referenceId) {
      return;
    }
    const response = terminationReason
      ? await patchTerminationReason(uid!, status, { assignable, name, onExportMapTo, referenceId, eligibleForRehire })
      : await addTerminationReason({
          eligibleForRehire,
          referenceId,
          onExportMapTo,
          name,
          assignable
        });
    if (response) onDone(response);
    onClose();
  }, [addTerminationReason, onDone, onClose, terminationReason, patchTerminationReason, localTerminationReason]);
  const isUtmostResource =
    localTerminationReason.sourceName === 'UTMOST' || localTerminationReason.sourceName === undefined;
  return (
    <Dialog
      actions={[
        <Button
          dataTest="confirm-button"
          variant="contained"
          key="confirm-button"
          color="primary"
          disabled={isAdding || isPatching}
          onClick={handleSubmitForm}
        >
          {intl.submit}
        </Button>
      ]}
      dataTest="create-termination-reason-dialog"
      disableBackdropClick
      onClose={onClose}
      open
      show={{ cancelButton: true }}
      subTitle={terminationReason ? intl.updateSubTitle : intl.addSubTitle}
      title={terminationReason ? intl.updateTitle : intl.addTitle}
    >
      <StyledInput
        dataTest="name-input"
        error={formSubmitted && !localTerminationReason.name}
        fullWidth
        label={intl.name}
        onChange={(event) =>
          setLocalTerminationReason({
            ...localTerminationReason,
            name: event.target.value,
            // Auto-generate suggested reference id based on the initial name.
            ...(!terminationReason?.referenceId ? { referenceId: convertToSnakeCase(event.target.value) } : {})
          })
        }
        required
        value={localTerminationReason.name || ''}
      />
      <StyledInput
        dataTest="reference-id-input"
        disabled={!isUtmostResource || !!terminationReason?.referenceId}
        error={formSubmitted && !localTerminationReason.referenceId}
        fullWidth
        helperText={intl.referenceIdCannotBeEdited}
        label={intl.referenceId}
        onChange={(event) => setLocalTerminationReason({ ...localTerminationReason, referenceId: event.target.value })}
        required
        value={localTerminationReason.referenceId || ''}
      />

      <StyledMuiSelect<boolean>
        dataTest="assignable-input"
        disableClearable
        label={intl.assignable}
        onChange={(option) => {
          setLocalTerminationReason({ ...localTerminationReason, assignable: option || false });
        }}
        options={options}
        value={localTerminationReason.assignable}
      />

      <StyledMuiSelect<boolean>
        dataTest="rehire-input"
        disableClearable
        label={intl.eligibleForRehire}
        onChange={(option) => {
          setLocalTerminationReason({ ...localTerminationReason, eligibleForRehire: option || false });
        }}
        options={eligibilityOptions}
        value={localTerminationReason.eligibleForRehire}
      />

      {terminationReason && (
        <StyledMuiSelect<StatusModel>
          dataTest="status-input"
          disableClearable
          disabled={!isUtmostResource}
          label={intl.status}
          onChange={(option) => {
            setLocalTerminationReason({ ...localTerminationReason, status: option || 'ACTIVE' });
          }}
          options={activeOptions}
          value={activeOptions.find((activeOption) => activeOption.value === localTerminationReason.status)?.value}
        />
      )}
    </Dialog>
  );
};
const StyledInput = styled((props: any) => <Input {...props} formClassName={props.className} />)`
  &&.MuiFormControl-root {
    margin-bottom: 1rem;
  }
` as typeof Input;
const StyledMuiSelect = styled(Select)`
  && {
    margin-bottom: 1.5rem;
  }
` as typeof Select;
const options: OptionTypeBase<boolean>[] = [
  { label: 'Enabled', value: true },
  { label: 'Disabled', value: false }
];
const eligibilityOptions: OptionTypeBase<boolean>[] = [
  { label: 'Yes', value: true },
  { label: 'No', value: false }
];
const activeOptions: OptionTypeBase<StatusModel>[] = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' }
];
